import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, Badge, Btn, BmModal, Card, Checkbox, Chip, EmptyState, Loading } from '../../ForBrands/ui/BrandUI';
import { API_BASE_URL } from '../../../constants';

// Possible duplicate athletes, for admins and Cache AI staff. Each pair is
// shown side by side. Dismiss closes the pair for good; Merge keeps the
// profile the admin picks and archives the other one, and the server then
// closes every other open pair of the archived profile. Nothing is merged
// without an admin choosing to. The profile that stays keeps its own team
// and the other one's main team becomes history, unless the admin ticks
// "Use the other profile's team as the main team" (useMergedTeam).

export const DUPLICATES_URL = `${API_BASE_URL}/admin/athletes/duplicates/`;
export const MERGE_URL = `${API_BASE_URL}/admin/athletes/merge/`;

// fetch, as the rest of the Users page, with the signed-in token. A refusal
// throws an error carrying the status and the server's body.
export const requestJson = async (method, url, body) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error('request_failed');
    err.status = res.status;
    err.data = data || {};
    throw err;
  }
  return data || {};
};

export const LEVEL_LABEL = { strong: 'Strong', likely: 'Likely', possible: 'Possible', name: 'Name only' };
const LEVEL_TONE = { strong: 'critical', likely: 'attention', possible: 'soft', name: 'passive' };

const LEVEL_FILTERS = [
  { value: '', label: 'All levels' },
  { value: 'strong', label: 'Strong' },
  { value: 'likely', label: 'Likely' },
  { value: 'possible', label: 'Possible' },
  { value: 'name', label: 'Name only' },
];
const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'claim_requested', label: 'Athlete asked' },
  { value: 'import_skipped', label: 'Skipped imports' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'merged', label: 'Merged' },
  { value: 'all', label: 'All' },
];

const SIGNAL_LABEL = {
  dob: 'Same birthday',
  same_dob: 'Same birthday',
  player_id: 'Same roster player',
  same_player_id: 'Same roster player',
  phone: 'Same phone',
  same_phone: 'Same phone',
  email: 'Same mailbox',
  mailbox: 'Same mailbox',
  same_email: 'Same mailbox',
  grad_year: 'Same grad year',
  gradyear: 'Same grad year',
  same_grad_year: 'Same grad year',
  team: 'Same team',
  team_overlap: 'Same team',
  hometown: 'Same hometown',
  same_hometown: 'Same hometown',
  name: 'Name match',
};

const TRIGGER_LABEL = {
  signup: 'At sign-up',
  onboarding: 'At onboarding',
  onboarding_complete: 'At onboarding',
  onboarding_patch: 'Profile details',
  team_added: 'Team added',
  sweep: 'Admin sweep',
  import: 'Import',
  claim: 'Athlete asked',
  manual: 'Manual',
};

const humanize = (v) => {
  const s = String(v || '').replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

export const signalLabel = (signal) => {
  const key = typeof signal === 'string' ? signal : signal?.kind || signal?.type || signal?.name || '';
  return SIGNAL_LABEL[String(key).toLowerCase()] || humanize(key);
};

const cohortOf = (s) => String(s?.cohort || '').toLowerCase();
export const cohortLabel = (s) => {
  const c = cohortOf(s);
  if (c === 'live') return 'Live';
  if (c === 'archived') return 'Archived';
  if (c === 'import') return 'Import row';
  return c ? 'Pilot' : '';
};
const COHORT_TONE = { Live: 'outline', Pilot: 'soft', Archived: 'passive', 'Import row': 'attention' };

// Merging needs an athlete record on both sides; an Excel row that was not
// imported has none.
export const canMergePair = (pair) => !!(pair?.a?.athleteId && pair?.b?.athleteId) && pair?.kind !== 'import';

const nameOf = (s) => s?.name || s?.email || 'This profile';

const fmtDate = (v) => {
  if (!v) return '';
  // A bare YYYY-MM-DD (birthdays) is shown as stored, never shifted by a time zone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return String(v);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const teamsText = (teams) => (Array.isArray(teams) ? teams : [])
  .map((t) => (typeof t === 'string' ? t : t?.teamName || t?.name || ''))
  .filter(Boolean)
  .join(', ');

const yesNo = (v) => (v ? 'Yes' : 'No');

const ROWS = [
  { label: 'Email', get: (s) => s.email || '' },
  { label: 'Earlier email', get: (s) => s.originalEmail || '', optional: true },
  { label: 'Teams', get: (s) => teamsText(s.teams) },
  { label: 'School', get: (s) => s.school || '' },
  { label: 'Hometown', get: (s) => s.hometown || '' },
  { label: 'Grad year', get: (s) => (s.gradYear ? String(s.gradYear) : '') },
  { label: 'Birthday', get: (s) => fmtDate(s.dob) },
  { label: 'Roster player', get: (s) => s.playerId || '' },
  { label: 'Stats', get: (s) => yesNo(s.hasStats) },
  { label: 'Socials', get: (s) => yesNo(s.hasSocials) },
  { label: 'Created', get: (s) => fmtDate(s.created) },
  { label: 'Last sign-in', get: (s) => fmtDate(s.lastLogin) },
];

// Which side to keep by default: the only live profile, else none (the
// admin picks).
export const defaultSurvivor = (pair) => {
  const aLive = cohortOf(pair?.a) === 'live';
  const bLive = cohortOf(pair?.b) === 'live';
  if (aLive && !bLive) return 'a';
  if (bLive && !aLive) return 'b';
  return null;
};

// The merge's team rule (10/19 notes, D8): the profile that stays keeps the
// team it chose; the other profile's main team becomes history, unless the
// admin makes it the main team.
export const mergeSummary = (survivor, merged, { useMergedTeam = false } = {}) => {
  const keep = nameOf(survivor);
  const keepEmail = survivor?.email ? ` (${survivor.email})` : '';
  const gone = merged?.email || nameOf(merged);
  const team = useMergedTeam
    ? `The main team on ${gone} becomes the main team of the profile that stays. Its own team, if it has one, stays on as another team.`
    : `The profile that stays keeps its own team. The main team on ${gone} is kept as history (a previous club or school), or becomes the main team only when the profile that stays has no team at all. Other teams on ${gone} are added as another team (up to 4, and not if already listed).`;
  return [
    `${keep}${keepEmail} stays.`,
    `Only the fields it is missing are filled from ${gone}. Nothing it already has is overwritten, and stats are not copied.`,
    team,
    `${gone} is archived with a link to ${keep}, hidden from the Player Index, and a live account there is signed out and locked.`,
  ].join(' ');
};

// The other profile has a team the admin could make the main team: a main
// team with an id and a league (mainTeamLinked). A team known by its name
// alone always goes to history, so the choice is not offered for it.
export const canUseMergedTeam = (merged) => merged?.mainTeamLinked === true;

// One plain sentence for what the merge did with the teams.
export const mergeTeamMessage = (data = {}) => {
  if (data.primaryTeamSet) return 'Their team is now its main team.';
  if (data.teamToHistory) return 'Their team was kept as history.';
  if (data.teamAdded) return 'Their team was added to the profile.';
  return '';
};

const serverMessage = (err, fallback) => {
  const data = err?.data || {};
  const text = [data.message, data.error, data.detail].find((v) => typeof v === 'string' && v.trim() && !/^[a-z0-9_.-]+$/.test(v));
  return text || fallback;
};

const SideHeader = ({ summary, side }) => {
  const cohort = cohortLabel(summary);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar name={nameOf(summary)} src={summary?.avatar || ''} size={32} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate bm-label-l text-bm-ink">{nameOf(summary)}</span>
          {cohort && <Badge tone={COHORT_TONE[cohort] || 'outline'}>{cohort}</Badge>}
        </div>
        <div className="bm-body-s text-bm-tertiary">Profile {side.toUpperCase()}</div>
      </div>
    </div>
  );
};

const PairCard = ({ pair, onDismiss, onMerge, busy }) => {
  const a = pair.a || {};
  const b = pair.b || {};
  // Skipped import rows can be dismissed too (never merged: canMergePair).
  const actionable = ['open', 'claim_requested', 'import_skipped'].includes(pair.status || 'open');
  const mergeable = canMergePair(pair);
  return (
    <Card padding="p-0" className="overflow-hidden" data-testid={`pair-${pair.id}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-bm-border px-4 py-3">
        <Badge tone={LEVEL_TONE[pair.level] || 'outline'}>{LEVEL_LABEL[pair.level] || humanize(pair.level) || 'Possible'}</Badge>
        {pair.status === 'claim_requested' && <Badge tone="brand">Athlete says this is their old profile</Badge>}
        {pair.kind === 'import' && <Badge tone="attention">Import row, not added</Badge>}
        {[...new Set((pair.signals || []).map(signalLabel).filter(Boolean))].map((label) => (
          <Badge key={label} tone="outline">{label}</Badge>
        ))}
        {pair.trigger && <span className="ml-auto bm-body-s text-bm-tertiary">{TRIGGER_LABEL[pair.trigger] || humanize(pair.trigger)}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-b border-bm-border">
              <th className="w-[120px] px-4 py-2.5" aria-label="Field" />
              <th className="px-4 py-2.5"><SideHeader summary={a} side="a" /></th>
              <th className="px-4 py-2.5"><SideHeader summary={b} side="b" /></th>
            </tr>
          </thead>
          <tbody>
            {ROWS.filter((row) => !row.optional || row.get(a) || row.get(b)).map((row) => {
              const va = row.get(a);
              const vb = row.get(b);
              const same = va && vb && va.toLowerCase() === vb.toLowerCase() && !['Stats', 'Socials'].includes(row.label);
              return (
                <tr key={row.label} className="border-b border-bm-border last:border-0">
                  <td className="px-4 py-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">{row.label}</td>
                  <td className={`break-all px-4 py-2 bm-body-s ${same ? 'font-semibold text-bm-ink' : 'text-bm-n700'}`}>{va || <span className="text-bm-tertiary">-</span>}</td>
                  <td className={`break-all px-4 py-2 bm-body-s ${same ? 'font-semibold text-bm-ink' : 'text-bm-n700'}`}>{vb || <span className="text-bm-tertiary">-</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {actionable && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-bm-border bg-bm-subtle px-4 py-3">
          <Btn size="sm" variant="secondary" onClick={() => onDismiss(pair)} disabled={busy}>Dismiss</Btn>
          {mergeable && <Btn size="sm" onClick={() => onMerge(pair)} disabled={busy}>Merge</Btn>}
        </div>
      )}
    </Card>
  );
};

const DuplicatePairsPanel = () => {
  const [status, setStatus] = useState('open');
  const [level, setLevel] = useState('');
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dismissing, setDismissing] = useState(null);
  const [merging, setMerging] = useState(null);
  const [survivorSide, setSurvivorSide] = useState(null);
  const [useMergedTeam, setUseMergedTeam] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ status, level }).toString();
      const data = await requestJson('GET', `${DUPLICATES_URL}?${qs}`);
      setPairs(data.pairs || []);
    } catch (err) {
      setPairs([]);
      setError(serverMessage(err, 'Could not load possible duplicates.'));
    } finally {
      setLoading(false);
    }
  }, [status, level]);

  useEffect(() => { load(); }, [load]);

  const dropPair = (id) => setPairs((prev) => prev.filter((p) => p.id !== id));

  const confirmDismiss = async () => {
    const pair = dismissing;
    if (!pair) return;
    setBusy(true);
    try {
      await requestJson('POST', `${DUPLICATES_URL}${encodeURIComponent(pair.id)}/dismiss/`, {});
      dropPair(pair.id);
      toast.success('Pair dismissed. Neither profile changed.');
      setDismissing(null);
    } catch (err) {
      toast.error(serverMessage(err, 'Could not dismiss this pair.'));
    } finally {
      setBusy(false);
    }
  };

  const openMerge = (pair) => {
    setSurvivorSide(defaultSurvivor(pair));
    setUseMergedTeam(false);
    setMerging(pair);
  };

  const pickSurvivor = (side) => {
    setSurvivorSide(side);
    setUseMergedTeam(false);
  };

  const confirmMerge = async () => {
    const pair = merging;
    if (!pair || !survivorSide) return;
    const survivor = survivorSide === 'a' ? pair.a : pair.b;
    const merged = survivorSide === 'a' ? pair.b : pair.a;
    setBusy(true);
    try {
      const body = { survivorId: survivor.athleteId, mergedId: merged.athleteId };
      if (useMergedTeam) body.useMergedTeam = true;
      const data = await requestJson('POST', MERGE_URL, body);
      const filled = Array.isArray(data.filled) ? data.filled : [];
      const parts = [`Merged into ${nameOf(survivor)}.`];
      parts.push(filled.length ? `Filled: ${filled.join(', ')}.` : 'No fields needed filling.');
      // primaryTeamSet: the main team; teamToHistory: kept as history (the
      // profile that stays had its own team); teamAdded: another team.
      const teamLine = mergeTeamMessage(data);
      if (teamLine) parts.push(teamLine);
      toast.success(parts.join(' '));
      dropPair(pair.id);
      setMerging(null);
      // The server also closes every other open pair of the merged profile
      // (superseded), so the list is fetched again rather than trimmed.
      load();
    } catch (err) {
      toast.error(serverMessage(err, 'Could not merge these profiles.'));
    } finally {
      setBusy(false);
    }
  };

  const mergeSides = merging ? [['a', merging.a || {}], ['b', merging.b || {}]] : [];
  const bothLive = merging && cohortOf(merging.a) === 'live' && cohortOf(merging.b) === 'live';

  return (
    <div className="w-full min-w-0 max-w-full">
      <Card padding="p-5" className="mb-4">
        <p className="bm-body-s text-bm-muted">
          Profiles that look like the same athlete, found by name plus birthday, team, grad year, hometown or contact details. Nothing is merged until you choose to.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((s) => (
            <Chip key={s.value} active={status === s.value} aria-pressed={status === s.value} onClick={() => setStatus(s.value)}>{s.label}</Chip>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {LEVEL_FILTERS.map((l) => (
            <Chip key={l.value || 'all'} active={level === l.value} aria-pressed={level === l.value} onClick={() => setLevel(l.value)}>{l.label}</Chip>
          ))}
        </div>
      </Card>

      {loading ? <Loading label="Loading possible duplicates..." /> : error ? (
        <EmptyState action={<Btn size="sm" variant="secondary" onClick={load}>Try again</Btn>}>{error}</EmptyState>
      ) : pairs.length === 0 ? (
        <EmptyState>No possible duplicates here.</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {pairs.map((pair) => (
            <PairCard key={pair.id} pair={pair} busy={busy} onDismiss={setDismissing} onMerge={openMerge} />
          ))}
        </div>
      )}

      {dismissing && (
        <BmModal
          title="Dismiss this pair"
          onClose={() => !busy && setDismissing(null)}
          width={460}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setDismissing(null)} disabled={busy}>Cancel</Btn>
              <Btn onClick={confirmDismiss} disabled={busy}>{busy ? 'Working...' : 'Dismiss'}</Btn>
            </>
          )}
        >
          <p className="bm-body-m text-bm-ink">
            {nameOf(dismissing.a)} and {nameOf(dismissing.b)} are not the same person? The pair is closed and not suggested again. Neither profile changes.
          </p>
        </BmModal>
      )}

      {merging && (
        <BmModal
          title="Merge profiles"
          onClose={() => !busy && setMerging(null)}
          width={560}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setMerging(null)} disabled={busy}>Cancel</Btn>
              <Btn onClick={confirmMerge} disabled={busy || !survivorSide} className="!bg-bm-critical hover:!bg-[#c41d14]">
                {busy ? 'Merging...' : 'Merge'}
              </Btn>
            </>
          )}
        >
          <fieldset>
            <legend className="mb-2 bm-label-l text-bm-ink">Which profile stays?</legend>
            <div className="flex flex-col gap-2">
              {mergeSides.map(([side, s]) => (
                <label
                  key={side}
                  className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-4 py-3 ${survivorSide === side ? 'border-bm-orange bg-bm-subtle' : 'border-bm-border'}`}
                >
                  <input
                    type="radio"
                    name="merge-survivor"
                    value={side}
                    checked={survivorSide === side}
                    onChange={() => pickSurvivor(side)}
                    className="mt-1 accent-bm-orange"
                    aria-label={`Keep ${nameOf(s)}${s.email ? ` (${s.email})` : ''}`}
                  />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="bm-label-l text-bm-ink">Keep {nameOf(s)}</span>
                      {cohortLabel(s) && <Badge tone={COHORT_TONE[cohortLabel(s)] || 'outline'}>{cohortLabel(s)}</Badge>}
                    </span>
                    <span className="block break-all bm-body-s text-bm-muted">{s.email}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {bothLive && (
            <p className="mt-3 rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2 bm-body-s text-bm-ink">
              Both are live accounts. The one you do not keep is signed out and locked.
            </p>
          )}
          {survivorSide && canUseMergedTeam(survivorSide === 'a' ? merging.b : merging.a) && (
            <div className="mt-3">
              <Checkbox
                checked={useMergedTeam}
                onChange={(v) => setUseMergedTeam(!!v)}
                label="Use the other profile's team as the main team"
              />
            </div>
          )}
          <p className="mt-3 bm-body-s text-bm-muted" data-testid="merge-summary">
            {survivorSide
              ? mergeSummary(survivorSide === 'a' ? merging.a : merging.b, survivorSide === 'a' ? merging.b : merging.a,
                { useMergedTeam })
              : 'Pick the profile that stays.'}
          </p>
        </BmModal>
      )}
    </div>
  );
};

export default DuplicatePairsPanel;
